import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

const Buy3BHKIndirapuramPage = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "Is upgrading from 2BHK to 3BHK in Indirapuram worth it?",
      answer: "Absolutely. 3BHK units offer 40-60% more living space (1,400-2,000 sq.ft vs 900-1,200 sq.ft), providing a dedicated guest room, home office, or kids' play area. While the price premium is 30-50% over 2BHK, the long-term appreciation is stronger (6-9% vs 5-7%), and resale liquidity remains excellent for premium 3BHK units in societies like ATS One Hamlet and Gaur City."
    },
    {
      question: "What is the ideal budget for a 3BHK flat in Indirapuram in 2026?",
      answer: "Budget ₹114 lakhs to ₹2.16 crores depending on society and amenities. Mid-range options like Shipra Sun City and Aditya Urban Homes cost ₹114L-1.44Cr, while premium properties in ATS One Hamlet and Gaur City range from ₹1.56-2.16Cr. With 99prop.com's zero brokerage model, you save ₹1.2-4.3L on these transactions."
    },
    {
      question: "Which societies in Indirapuram offer the best 3BHK options for families?",
      answer: "For families: ATS One Hamlet (premium amenities, ₹1.56-2.16Cr), Gaur City (township living, ₹1.32-1.68Cr), Shipra Sun City (established community, ₹1.2-1.56Cr), and Aditya Urban Homes (value option, ₹114L-1.44Cr). All offer good schools nearby, parks, and family-friendly environments."
    },
    {
      question: "How does 99prop.com's direct buying save money on 3BHK purchases?",
      answer: "Traditional brokers charge 1-2% commission. On a ₹1 crore 3BHK, that's ₹1-2 lakhs. 99prop.com owns or has exclusive agreements on properties, eliminating this cost entirely. You also get free legal verification, investment advisory, and home loan assistance—services brokers charge extra for."
    },
    {
      question: "What is the rental income potential for 3BHK in Indirapuram?",
      answer: "3BHK units command ₹25,000-45,000 monthly rent depending on society and furnishing. Premium furnished units in ATS can fetch ₹40-45K, while unfurnished units in mid-range societies rent for ₹25-30K. This translates to 3-4% rental yields with strong occupancy due to demand from families and expats."
    },
    {
      question: "Should I buy 3BHK now or wait for market correction?",
      answer: "Buy now if you have end-use need. Indirapuram's established infrastructure and consistent demand prevent sharp corrections. Waiting costs you rental savings (₹3-5L annually) and appreciation (6-9% yearly). With 99prop.com's zero brokerage and transparent pricing, you're already getting 1-2% better value than market rates."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": "99Prop",
    "url": "https://99prop.com",
    "telephone": "+91-9891979097",
    "address": { "@type": "PostalAddress", "addressLocality": "Indirapuram", "addressRegion": "Ghaziabad", "addressCountry": "IN" },
    "priceRange": "₹95L - ₹1.8Cr",
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "520" }
  };

  return (
    <>
      <Helmet>
        <title>Buy 3BHK in Indirapuram | Premium Apartments | Zero Brokerage</title>
        <meta name="description" content="Buy spacious 3BHK flats in Indirapuram from ₹95L-₹1.8Cr. Zero brokerage, direct deals. Perfect for families & upgrade buyers. ATS, Gaur City, Shipra properties." />
        <meta name="keywords" content="buy 3BHK Indirapuram, 3BHK flat Indirapuram, upgrade to 3BHK, premium apartments Indirapuram, ATS One Hamlet 3BHK, zero brokerage" />
        <link rel="canonical" href="https://99prop.com/buy-3bhk-in-indirapuram" />
        
        <meta property="og:title" content="Buy 3BHK in Indirapuram | Premium Apartments | Zero Brokerage" />
        <meta property="og:description" content="Buy spacious 3BHK flats in Indirapuram from ₹95L-₹1.8Cr. Zero brokerage, direct deals. Perfect for families & upgrade buyers." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://99prop.com/buy-3bhk-in-indirapuram" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Buy 3BHK in Indirapuram | Premium Apartments | Zero Brokerage" />
      </Helmet>
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }} />

      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-brand-500 to-brand-700 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">Buy 3BHK in Indirapuram</h1>
            <p className="text-xl sm:text-2xl text-brand-100 mb-8 max-w-3xl mx-auto">
              Premium Living | Zero Brokerage | 1,400-2,000 sq.ft | ₹114L-₹2.16Cr
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => navigate('/buy-property')} className="bg-white text-brand-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl">
                View 3BHK Properties
              </button>
              <a href="tel:+919891979097" className="bg-brand-800 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-900 transition shadow-xl">
                Call: +91 9891 97 90 97
              </a>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <article className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 space-y-8">
            
            <section>
              <p className="text-lg text-gray-700 leading-relaxed">
                Looking to <strong>buy 3BHK in Indirapuram</strong> and upgrade from your current 2BHK? 99prop.com is your direct buying platform for premium properties—not a listing portal. We specialize in spacious 3BHK units (1,400-2,000 sq.ft) perfect for growing families, upgrade buyers, and those seeking long-term capital appreciation. With zero brokerage and exclusive inventory in societies like ATS One Hamlet, Gaur City, and Shipra Sun City, we help you transition to premium living without the hefty commission fees.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">3BHK Price Trends in Indirapuram (2026)</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The 3BHK segment in Indirapuram has shown consistent appreciation, outperforming 2BHK units due to higher demand from families and upgrade buyers. Here's the current pricing landscape:
              </p>
              <div className="bg-brand-50 p-6 rounded-xl space-y-4">
                <div className="border-l-4 border-brand-600 pl-4">
                  <h3 className="text-lg font-bold text-gray-900">Entry Premium: ₹72-90 Lakhs</h3>
                  <p className="text-gray-700">Aditya Urban Homes, Amrapali Silicon City. 1,400-1,600 sq.ft. Ideal for first-time 3BHK buyers.</p>
                </div>
                <div className="border-l-4 border-brand-600 pl-4">
                  <h3 className="text-lg font-bold text-gray-900">Mid-Premium: ₹90-114 Lakhs</h3>
                  <p className="text-gray-700">Shipra Sun City, Nyay Khand societies. 1,500-1,800 sq.ft. Established communities, good appreciation.</p>
                </div>
                <div className="border-l-4 border-brand-600 pl-4">
                  <h3 className="text-lg font-bold text-gray-900">Ultra-Premium: ₹1.56-2.16 Crores</h3>
                  <p className="text-gray-700">ATS One Hamlet, Gaur City. 1,700-2,000 sq.ft. Luxury amenities, highest appreciation potential (7-9% annually).</p>
                </div>
              </div>
              <p className="text-gray-700 mt-4">
                <strong>Market Insight:</strong> 3BHK units priced ₹1Cr+ show stronger appreciation (6-9% yearly) compared to 2BHK (5-7%), making them superior long-term investments.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Best Societies to Buy 3BHK in Indirapuram</h2>
              <div className="space-y-6">
                <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-brand-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">1. ATS One Hamlet</h3>
                  <p className="text-gray-700 mb-2"><strong>Price:</strong> ₹1.56-2.16Cr | <strong>Size:</strong> 1,700-2,000 sq.ft | <strong>Possession:</strong> Ready</p>
                  <p className="text-gray-700">
                    Ultra-premium gated community with clubhouse, swimming pool, gym, landscaped gardens. Strong resale value, excellent for HNI families. Appreciation: 8-9% annually.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-brand-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">2. Gaur City</h3>
                  <p className="text-gray-700 mb-2"><strong>Price:</strong> ₹1.32-1.68Cr | <strong>Size:</strong> 1,550-1,850 sq.ft | <strong>Possession:</strong> Ready</p>
                  <p className="text-gray-700">
                    Integrated township with schools, hospitals, malls within complex. Perfect for families seeking self-contained living. Strong rental demand (₹30-40K/month).
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-brand-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">3. Shipra Sun City</h3>
                  <p className="text-gray-700 mb-2"><strong>Price:</strong> ₹84-108L | <strong>Size:</strong> 1,450-1,750 sq.ft | <strong>Possession:</strong> Ready</p>
                  <p className="text-gray-700">
                    Mature society with established community, good maintenance. Metro connectivity. Balanced option for upgrade buyers. Appreciation: 6-7% annually.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-brand-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">4. Aditya Urban Homes</h3>
                  <p className="text-gray-700 mb-2"><strong>Price:</strong> ₹60-80L | <strong>Size:</strong> 1,350-1,600 sq.ft | <strong>Possession:</strong> Ready</p>
                  <p className="text-gray-700">
                    Entry-level 3BHK option. Good connectivity, decent amenities. Popular among young families upgrading from 2BHK. Value-for-money proposition.
                  </p>
                </div>
              </div>
            </section>

            <section className="bg-gradient-to-r from-brand-500 to-brand-600 p-8 rounded-xl text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Explore Premium 3BHK Properties</h3>
              <p className="text-brand-100 mb-6">Zero brokerage. Direct buying. Save ₹1-2 lakhs on commission.</p>
              <button onClick={() => navigate('/buy-property')} className="bg-white text-brand-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl">
                View 3BHK Inventory
              </button>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Who Should Buy 3BHK? Understanding the Buyer Persona</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                3BHK properties in Indirapuram attract a distinct buyer profile compared to 2BHK units. Understanding if you fit this persona helps make the right investment decision:
              </p>
              
              <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">👨‍👩‍👧‍👦 Growing Families (Primary Segment)</h3>
                  <p className="text-gray-700 mb-2"><strong>Profile:</strong> Couples with 2+ children or planning to expand family. Need separate rooms for kids, guest room for elderly parents.</p>
                  <p className="text-gray-700"><strong>Why 3BHK:</strong> 2BHK becomes cramped with growing kids. Extra room provides privacy, study space, and future-proofing.</p>
                </div>

                <div className="bg-blue-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">💼 Work-From-Home Professionals</h3>
                  <p className="text-gray-700 mb-2"><strong>Profile:</strong> Individuals or couples needing dedicated home office. Remote workers, entrepreneurs, consultants.</p>
                  <p className="text-gray-700"><strong>Why 3BHK:</strong> Third bedroom converts to professional workspace, separating work and personal life. Essential for productivity and client calls.</p>
                </div>

                <div className="bg-blue-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">📈 Upgrade Buyers (High Intent)</h3>
                  <p className="text-gray-700 mb-2"><strong>Profile:</strong> Current 2BHK owners in Indirapuram or nearby areas. Income growth justifies upgrade.</p>
                  <p className="text-gray-700"><strong>Why 3BHK:</strong> Better capital appreciation (6-9% vs 5-7%), superior rental yields if old 2BHK is rented out. Status upgrade.</p>
                </div>

                <div className="bg-blue-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">🏆 HNI Investors</h3>
                  <p className="text-gray-700 mb-2"><strong>Profile:</strong> Investors with ₹1.3Cr+ budget seeking premium appreciation and rental income.</p>
                  <p className="text-gray-700"><strong>Why 3BHK:</strong> Better liquidity than 4BHK, attracts high-quality tenants (expats, senior executives), lower vacancy risk.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">3BHK vs 2BHK: Complete Comparison</h2>
              <p className="text-gray-700 mb-4">Deciding between 2BHK and 3BHK? Here's a data-driven comparison:</p>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead className="bg-brand-500 text-white">
                    <tr>
                      <th className="border border-gray-300 px-4 py-3 text-left">Parameter</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">2BHK</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">3BHK</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Carpet Area</td>
                      <td className="border border-gray-300 px-4 py-3">900-1,200 sq.ft</td>
                      <td className="border border-gray-300 px-4 py-3">1,400-2,000 sq.ft</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Price Range (Indirapuram)</td>
                      <td className="border border-gray-300 px-4 py-3">₹48-90L</td>
                      <td className="border border-gray-300 px-4 py-3">₹114L-₹2.16Cr</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Annual Appreciation</td>
                      <td className="border border-gray-300 px-4 py-3">5-7%</td>
                      <td className="border border-gray-300 px-4 py-3">6-9%</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Monthly Rent</td>
                      <td className="border border-gray-300 px-4 py-3">₹15-25K</td>
                      <td className="border border-gray-300 px-4 py-3">₹25-45K</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Rental Yield</td>
                      <td className="border border-gray-300 px-4 py-3">3-4%</td>
                      <td className="border border-gray-300 px-4 py-3">3.5-4.5%</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Target Buyers</td>
                      <td className="border border-gray-300 px-4 py-3">Young couples, small families</td>
                      <td className="border border-gray-300 px-4 py-3">Growing families, HNIs, upgrade buyers</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Resale Time</td>
                      <td className="border border-gray-300 px-4 py-3">30-60 days</td>
                      <td className="border border-gray-300 px-4 py-3">45-75 days</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Maintenance Cost</td>
                      <td className="border border-gray-300 px-4 py-3">₹2,500-4,000/month</td>
                      <td className="border border-gray-300 px-4 py-3">₹4,000-7,000/month</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Best For</td>
                      <td className="border border-gray-300 px-4 py-3">Budget-conscious, rental income</td>
                      <td className="border border-gray-300 px-4 py-3">Family living, long-term appreciation</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-green-50 p-6 rounded-xl mt-6">
                <h4 className="text-lg font-bold text-gray-900 mb-2">💡 Decision Rule</h4>
                <p className="text-gray-700">
                  Choose 3BHK if: (a) You have 3+ family members, (b) Need home office, (c) Budget allows ₹95L+, (d) Seeking premium appreciation. 
                  <a href="/buy-2bhk-in-indirapuram" className="text-brand-600 font-semibold hover:underline ml-1">
                    Still considering 2BHK? Compare options here →
                  </a>
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Investment Potential: Why 3BHK Outperforms</h2>
              <div className="space-y-4">
                <div className="border-l-4 border-brand-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Superior Capital Appreciation: 6-9% Annually</h3>
                  <p className="text-gray-700">
                    Premium 3BHK units in ATS and Gaur City have shown 7-9% annual appreciation (vs 5-7% for 2BHK). A ₹1.2Cr property today could be worth ₹1.65-1.8Cr in 5 years. Example: ATS One Hamlet 3BHK bought at ₹1Cr in 2020 now trades at ₹1.5-1.6Cr.
                  </p>
                </div>

                <div className="border-l-4 border-brand-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Higher Rental Income: ₹25-45K Monthly</h3>
                  <p className="text-gray-700">
                    3BHK units attract premium tenants—expat families, senior executives, joint families. Furnished units in ATS/Gaur City command ₹50-60K rent (₹6-7.2L annually), translating to 4.5-5% yields on ₹1.3Cr investment. Vacancy periods are shorter (15-30 days vs 30-45 for 2BHK).
                  </p>
                </div>

                <div className="border-l-4 border-brand-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Future-Proof Investment</h3>
                  <p className="text-gray-700">
                    As NCR salaries grow, demand shifts toward spacious homes. 3BHK units will become the new standard for middle-income families by 2030, similar to how 2BHK replaced 1BHK. Early buyers capture maximum appreciation.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why 99prop.com's Direct Buying Saves You ₹1-2 Lakhs</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                On a ₹1 crore 3BHK purchase, traditional brokers pocket ₹1-2 lakhs as commission—money that should go toward your interiors or furnishing. Here's how we eliminate this:
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-600 text-white rounded-full flex items-center justify-center font-bold text-xl">1</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">We Own Premium Inventory</h3>
                    <p className="text-gray-700">Direct ownership of select 3BHK units or exclusive agreements with builders. No broker layer = zero commission.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-600 text-white rounded-full flex items-center justify-center font-bold text-xl">2</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Transparent Pricing</h3>
                    <p className="text-gray-700">Our ₹1.4Cr price is ₹1.4Cr—not ₹1.4Cr property + ₹2.8L broker fee. What you see is what you pay.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-600 text-white rounded-full flex items-center justify-center font-bold text-xl">3</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Free Premium Services</h3>
                    <p className="text-gray-700">Investment analysis, legal verification, home loan coordination, Vastu consultation—all complimentary. Brokers charge ₹15-25K extra for these.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-600 text-white rounded-full flex items-center justify-center font-bold text-xl">4</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">7-15 Day Closures</h3>
                    <p className="text-gray-700">Direct dealing means no multi-party negotiations. Documentation to keys in under 2 weeks for ready-to-move units.</p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 p-6 rounded-xl mt-6">
                <h4 className="text-lg font-bold text-gray-900 mb-2">💰 Real Savings Example (2026)</h4>
                <p className="text-gray-700">
                  <strong>Property:</strong> 3BHK in Gaur City, 1,600 sq.ft<br/>
                  <strong>Market Price (via broker):</strong> ₹1.4Cr property + ₹2.8L brokerage (2%) = ₹1.428Cr total<br/>
                  <strong>99prop.com Price:</strong> ₹1.4Cr (direct deal, no broker)<br/>
                  <strong>Your Savings:</strong> ₹2.8 lakhs—enough for modular kitchen + wardrobes
                </p>
              </div>
            </section>

            <section className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Related Resources</h3>
              <ul className="space-y-2">
                <li><a href="/buy-property" className="text-brand-600 font-semibold hover:underline">→ Browse All 3BHK Properties</a></li>
                <li><a href="/buy-2bhk-in-indirapuram" className="text-brand-600 font-semibold hover:underline">→ Compare with 2BHK Options</a></li>
                <li><a href="/?start=true" className="text-brand-600 font-semibold hover:underline">→ Free Property Valuation Tool</a></li>
                <li><a href="/buyer-strategy" className="text-brand-600 font-semibold hover:underline">→ Get Personalized Buyer Strategy</a></li>
                <li><a href="/sell-flat-in-indirapuram" className="text-brand-600 font-semibold hover:underline">→ Planning to Sell Your Current Property?</a></li>
              </ul>
            </section>

            {/* WhatsApp CTA */}
            <section className="bg-gradient-to-r from-green-500 to-green-600 p-8 rounded-xl text-center">
              <h3 className="text-2xl font-bold text-white mb-3">Get Instant Quote on WhatsApp</h3>
              <p className="text-green-100 mb-6">Response under 5 minutes</p>
              <a href="https://wa.me/919891979097?text=Hi, interested in 3BHK Indirapuram" target="_blank" rel="noopener noreferrer" className="bg-white text-green-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl inline-flex items-center justify-center gap-2">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp Now
              </a>
            </section>

            {/* Trust + Objections */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Trusted by 1,200+ Buyers</h2>
              <div className="grid md:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-br from-brand-50 to-blue-50 p-6 rounded-xl text-center">
                  <div className="text-4xl font-bold text-brand-600">520+</div>
                  <div className="text-gray-700 font-semibold">Properties Sold</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl text-center">
                  <div className="text-4xl font-bold text-green-600">₹280Cr+</div>
                  <div className="text-gray-700 font-semibold">Transaction Value</div>
                </div>
                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl text-center">
                  <div className="text-4xl font-bold text-orange-600">4.8/5</div>
                  <div className="text-gray-700 font-semibold">Rating</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl text-center">
                  <div className="text-4xl font-bold text-purple-600">7-15</div>
                  <div className="text-gray-700 font-semibold">Days</div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-brand-500">
                  <h3 className="text-lg font-bold mb-2">❓ Market rate guarantee?</h3>
                  <p className="text-gray-700">Yes, AI-verified pricing. Save 1-2% brokerage.</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-green-500">
                  <h3 className="text-lg font-bold mb-2">❓ Why trust 99prop?</h3>
                  <p className="text-gray-700">520+ deals completed, ₹280Cr+ transactions.</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-blue-500">
                  <h3 className="text-lg font-bold mb-2">❓ Negotiation possible?</h3>
                  <p className="text-gray-700">Limited 2-3%. Already zero brokerage.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="border-b border-gray-200 pb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-brand-600 p-8 rounded-xl text-center">
              <h2 className="text-3xl font-bold text-white mb-4">Ready to Upgrade to 3BHK in Indirapuram?</h2>
              <p className="text-brand-100 text-lg mb-6">
                Zero brokerage. Direct deals. Premium properties. Expert advisory. Start your upgrade journey today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={() => navigate('/buy-property')} className="bg-white text-brand-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl">
                  View 3BHK Inventory
                </button>
                <a href="tel:+919891979097" className="bg-brand-800 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-900 transition shadow-xl">
                  Call: +91 9891 97 90 97
                </a>
              </div>
            </section>

          </article>
        </div>
      </div>
    </>
  );
};

export default Buy3BHKIndirapuramPage;
